import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { BUILD_TIMESTAMP, BUILD_VERSION } from '../BuildInfo';
import { isAdmin, isSuperAdmin } from '../components/common/UserMenu';
import NotificationPreferences from '../components/notifications/NotificationPreferences';
import WebhookManager from '../components/notifications/WebhookManager';
import AREA_CONFIGS from '../features/tenants/config/functionalAreaConfigs';
import {
  UserIcon,
  SecurityIcon,
  KeyIcon,
  SunIcon,
  MoonIcon,
  CheckIcon,
  AIIcon,
  CloudIcon,
  BellIcon
} from '../components/common/Icons';
import { IntegrationSecrets } from '../components/settings/IntegrationSecrets';

// Camera Icon (we need to add this)
const Component: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { theme, toggleTheme } = useTheme();

  const { user, authFetch, isLoading: authLoading, tenantId } = useAuth();
  const userRole = user?.role || '';
  const isAdminUser = isAdmin(userRole) || isSuperAdmin(userRole);

  const tabs = useMemo(() => [
    { id: 'profile', label: 'Profile', icon: UserIcon },
    { id: 'security', label: 'Security', icon: SecurityIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'appearance', label: 'Appearance', icon: SunIcon },
    { id: 'aichat', label: 'AI Chat', icon: AIIcon },
    ...(isAdminUser ? [
      { id: 'api', label: 'API Keys', icon: KeyIcon },
      { id: 'integrations', label: 'Integrations', icon: CloudIcon },
    ] : []),
  ], [isAdminUser]);

  // Reset to profile if current tab is hidden (e.g. role change)
  useEffect(() => {
    if (!tabs.find(t => t.id === activeTab)) {
      setActiveTab('profile');
    }
  }, [tabs, activeTab]);
  useEffect(() => {
    if (authLoading) return;
    authFetch("/api/auth/me").then(r => r.ok && r.json()).then(d => { if(d.email) setProfile(p => ({...p, email: d.email, name: d.name || p.name})); }).catch(e => console.error(e));
  }, [authFetch, authLoading]);
  // Profile state
  const [profile, setProfile] = useState({
    name: 'Admin User',
    email: 'admin@company.com',
    company: 'Acme Corporation',
    role: 'Platform Administrator',
    timezone: 'America/New_York',
    language: 'en'
  });

  // Landing area preference (WQ-087b)
  const [landingArea, setLandingArea] = useState<string>('');
  const [landingAreaSaving, setLandingAreaSaving] = useState(false);
  const hasTenantContext = !!tenantId;

  // Load landing area preference on mount
  useEffect(() => {
    if (authLoading || !hasTenantContext) return;
    authFetch('/api/tenant-portal/user/preferences')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.preferred_landing_area) setLandingArea(d.preferred_landing_area);
      })
      .catch(() => {/* ignore — user may not have tenant context */});
  }, [authFetch, authLoading, hasTenantContext]);

  const saveLandingArea = useCallback(async (areaKey: string) => {
    setLandingAreaSaving(true);
    try {
      const body = areaKey ? { preferred_landing_area: areaKey } : { preferred_landing_area: null };
      const res = await authFetch('/api/tenant-portal/user/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setLandingArea(areaKey);
      }
    } catch (e) {
      console.error('Failed to save landing area preference:', e);
    } finally {
      setLandingAreaSaving(false);
    }
  }, [authFetch]);

  // API Keys (mock)
  const apiKeys = [
    { id: 1, name: 'Production API Key', prefix: 'sk_live_', created: 'Jan 15, 2025', lastUsed: '2 min ago' },
    { id: 2, name: 'Development API Key', prefix: 'sk_test_', created: 'Dec 10, 2024', lastUsed: '1 hour ago' },
  ];

  // AI Chat settings (personal display preferences only — governance controls live in Administration → AI Policies)
  const [aiChatSettings, setAiChatSettings] = useState({
    defaultModel: 'claude-opus-4-5-20251101',
    enableThinkingVisibility: true,
    autoSuggestActions: true,
    streamResponses: true
  });

  const renderProfileTab = () => (
    <div style={{ maxWidth: '600px' }}>
      {/* Avatar Section */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-6)',
        marginBottom: 'var(--space-8)',
        padding: 'var(--space-6)',
        background: 'var(--glass-bg)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--glass-border)'
      }}>
        <div style={{ position: 'relative' }}>
          <div 
            className="avatar avatar-xl"
            style={{
              width: '80px',
              height: '80px',
              fontSize: 'var(--text-2xl)',
              background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%)'
            }}
          >
            {profile.name.charAt(0)}
          </div>
          <button
            className="btn btn-secondary btn-icon"
            style={{
              position: 'absolute',
              bottom: '-4px',
              right: '-4px',
              width: '32px',
              height: '32px',
              padding: 0,
              borderRadius: 'var(--radius-full)'
            }}
          >
            <Component size={14} />
          </button>
        </div>
        <div>
          <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '4px' }}>
            {profile.name}
          </h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            {profile.role}
          </p>
        </div>
      </div>

      {/* Profile Form */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div className="input-group">
          <label className="input-label">Full Name</label>
          <input
            type="text"
            className="input"
            value={profile.name}
            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
          />
        </div>

        <div className="input-group">
          <label className="input-label">Email Address</label>
          <input
            type="email"
            className="input"
            value={profile.email}
            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
          />
        </div>

        <div className="input-group">
          <label className="input-label">Company</label>
          <input
            type="text"
            className="input"
            value={profile.company}
            onChange={(e) => setProfile({ ...profile, company: e.target.value })}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
          <div className="input-group">
            <label className="input-label">Timezone</label>
            <select 
              className="input"
              value={profile.timezone}
              onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">GMT</option>
              <option value="Asia/Tokyo">Japan Standard Time</option>
            </select>
          </div>

          <div className="input-group">
            <label className="input-label">Language</label>
            <select 
              className="input"
              value={profile.language}
              onChange={(e) => setProfile({ ...profile, language: e.target.value })}
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </select>
          </div>
        </div>

        {/* Landing Page Preference — only shown for tenant users (WQ-087b) */}
        {hasTenantContext && (
          <div className="input-group" data-testid="landing-area-preference">
            <label className="input-label">Default Landing Page</label>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-2)' }}>
              Choose which area dashboard you see first when logging in.
            </p>
            <select
              className="input"
              value={landingArea}
              onChange={(e) => saveLandingArea(e.target.value)}
              disabled={landingAreaSaving}
              data-testid="landing-area-select"
            >
              <option value="">Auto (based on role)</option>
              {Object.entries(AREA_CONFIGS).map(([key, config]) => (
                <option key={key} value={key}>{config.areaName}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ paddingTop: 'var(--space-4)' }}>
          <button className="btn btn-primary">Save Changes</button>
        </div>
      </div>

      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: 'var(--space-6)', borderTop: '1px solid var(--glass-border)', paddingTop: 'var(--space-3)' }}>
        Build: {BUILD_VERSION} &middot; {BUILD_TIMESTAMP}
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div style={{ maxWidth: '600px' }}>
      {/* Password Section */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
          Change Password
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="input-group">
            <label className="input-label">Current Password</label>
            <input type="password" className="input" placeholder="Enter current password" />
          </div>
          <div className="input-group">
            <label className="input-label">New Password</label>
            <input type="password" className="input" placeholder="Enter new password" />
          </div>
          <div className="input-group">
            <label className="input-label">Confirm New Password</label>
            <input type="password" className="input" placeholder="Confirm new password" />
          </div>
          <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
            Update Password
          </button>
        </div>
      </div>

      {/* Two-Factor Auth */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '4px' }}>
              Two-Factor Authentication
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              Add an extra layer of security to your account
            </p>
          </div>
          <span className="badge badge-success">Enabled</span>
        </div>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <button className="btn btn-secondary">Manage 2FA</button>
        </div>
      </div>

      {/* Active Sessions */}
      <div className="card">
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
          Active Sessions
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {[
            { device: 'Chrome on Windows', location: 'New York, US', current: true },
            { device: 'Safari on macOS', location: 'San Francisco, US', current: false },
          ].map((session, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-3)',
                background: 'var(--glass-bg)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--glass-border)'
              }}
            >
              <div>
                <div style={{ fontWeight: 500, marginBottom: '2px' }}>
                  {session.device}
                  {session.current && (
                    <span className="badge badge-accent" style={{ marginLeft: 'var(--space-2)' }}>
                      Current
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {session.location}
                </div>
              </div>
              {!session.current && (
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }}>
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div style={{ maxWidth: '900px' }}>
      <NotificationPreferences />
      <WebhookManager />
    </div>
  );

  const renderApiKeysTab = () => (
    <div style={{ maxWidth: '800px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
        <div>
          <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '4px' }}>
            API Keys
          </h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            Manage your API keys for programmatic access
          </p>
        </div>
        <button className="btn btn-primary">Create New Key</button>
      </div>

      <div className="card card-elevated">
        <div className="table-container" style={{ background: 'transparent', border: 'none' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key) => (
                <tr key={key.id}>
                  <td style={{ fontWeight: 500 }}>{key.name}</td>
                  <td>
                    <code style={{
                      padding: 'var(--space-1) var(--space-2)',
                      background: 'var(--glass-bg)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'var(--font-mono)'
                    }}>
                      {key.prefix}••••••••••••
                    </code>
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{key.created}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{key.lastUsed}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{
        marginTop: 'var(--space-6)',
        padding: 'var(--space-4)',
        background: 'var(--warning-muted)',
        borderRadius: 'var(--radius-lg)',
        fontSize: 'var(--text-sm)',
        color: 'var(--warning)'
      }}>
        <strong>Security Note:</strong> Keep your API keys secure. Never share them in public repositories or client-side code.
      </div>
    </div>
  );

  const renderAppearanceTab = () => (
    <div style={{ maxWidth: '600px' }}>
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
          Theme
        </h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
          Choose your preferred color theme
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
          {/* Dark Theme */}
          <div
            onClick={() => toggleTheme()}
            style={{
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-xl)',
              border: `2px solid ${theme === 'dark' ? 'var(--accent-primary)' : 'var(--glass-border)'}`,
              background: theme === 'dark' ? 'var(--accent-primary-muted)' : 'var(--glass-bg)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)'
            }}
          >
            <div style={{
              width: '100%',
              height: '80px',
              background: '#0a0a0f',
              borderRadius: 'var(--radius-lg)',
              marginBottom: 'var(--space-3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MoonIcon size={24} style={{ color: '#ffffff' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 500 }}>Dark</span>
              {theme === 'dark' && <CheckIcon size={18} style={{ color: 'var(--accent-primary)' }} />}
            </div>
          </div>

          {/* Light Theme */}
          <div
            onClick={() => toggleTheme()}
            style={{
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-xl)',
              border: `2px solid ${theme === 'light' ? 'var(--accent-primary)' : 'var(--glass-border)'}`,
              background: theme === 'light' ? 'var(--accent-primary-muted)' : 'var(--glass-bg)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)'
            }}
          >
            <div style={{
              width: '100%',
              height: '80px',
              background: '#f8fafc',
              borderRadius: 'var(--radius-lg)',
              marginBottom: 'var(--space-3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SunIcon size={24} style={{ color: '#0f172a' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 500 }}>Light</span>
              {theme === 'light' && <CheckIcon size={18} style={{ color: 'var(--accent-primary)' }} />}
            </div>
          </div>
        </div>
      </div>

      {/* Accent Color (Future Feature) */}
      <div className="card">
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
          Accent Color
        </h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
          Choose your accent color (coming soon)
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          {['#00d4ff', '#7c3aed', '#10b981', '#f59e0b', '#ef4444'].map((color) => (
            <div
              key={color}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-full)',
                background: color,
                cursor: 'pointer',
                border: color === '#00d4ff' ? '3px solid var(--text-primary)' : 'none',
                opacity: color === '#00d4ff' ? 1 : 0.5
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderAiChatTab = () => (
    <div style={{ maxWidth: '600px' }}>
      {/* Model Selection */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
          Default Model
        </h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
          Choose your default AI model for chat conversations
        </p>
        <select
          className="input"
          value={aiChatSettings.defaultModel}
          onChange={(e) => setAiChatSettings({ ...aiChatSettings, defaultModel: e.target.value })}
          style={{ width: '100%' }}
        >
          <option value="claude-opus-4-5-20251101">Claude Opus 4.5 (Highest capability)</option>
          <option value="claude-sonnet-4-5-20251101">Claude Sonnet 4.5 (Balanced)</option>
          <option value="claude-haiku-4-5-20251101">Claude Haiku 4.5 (Fast)</option>
        </select>
      </div>

      {/* Display Settings */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
          Display Settings
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {/* Thinking Visibility */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--space-4)',
            background: 'var(--glass-bg)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--glass-border)'
          }}>
            <div>
              <div style={{ fontWeight: 500, marginBottom: '2px' }}>Show Thinking Process</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                Display Claude's thinking steps in chat
              </div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={aiChatSettings.enableThinkingVisibility}
                onChange={(e) => setAiChatSettings({ ...aiChatSettings, enableThinkingVisibility: e.target.checked })}
              />
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
            </label>
          </div>

          {/* Auto Suggest */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--space-4)',
            background: 'var(--glass-bg)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--glass-border)'
          }}>
            <div>
              <div style={{ fontWeight: 500, marginBottom: '2px' }}>Auto-suggest Actions</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                Show suggested follow-up actions
              </div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={aiChatSettings.autoSuggestActions}
                onChange={(e) => setAiChatSettings({ ...aiChatSettings, autoSuggestActions: e.target.checked })}
              />
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
            </label>
          </div>

          {/* Stream Responses */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--space-4)',
            background: 'var(--glass-bg)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--glass-border)'
          }}>
            <div>
              <div style={{ fontWeight: 500, marginBottom: '2px' }}>Stream Responses</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                Show responses as they're generated
              </div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={aiChatSettings.streamResponses}
                onChange={(e) => setAiChatSettings({ ...aiChatSettings, streamResponses: e.target.checked })}
              />
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ paddingTop: 'var(--space-4)' }}>
        <button className="btn btn-primary">Save AI Chat Settings</button>
      </div>
    </div>
  );

  const renderIntegrationsTab = () => (
    <IntegrationSecrets />
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile': return renderProfileTab();
      case 'security': return renderSecurityTab();
      case 'notifications': return renderNotificationsTab();
      case 'api': return renderApiKeysTab();
      case 'integrations': return renderIntegrationsTab();
      case 'appearance': return renderAppearanceTab();
      case 'aichat': return renderAiChatTab();
      default: return renderProfileTab();
    }
  };

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage your account and platform preferences</p>
        </div>
      </div>

      {/* Settings Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 'var(--space-8)' }}>
        {/* Settings Navigation */}
        <div className="card" style={{ padding: 'var(--space-3)', height: 'fit-content' }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  background: activeTab === tab.id ? 'var(--accent-primary-muted)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 500,
                  transition: 'all var(--transition-fast)'
                }}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Settings Content */}
        <div>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
